import {Component, inject, OnDestroy, ViewChild} from '@angular/core';
import {ModalImgComponent} from "./modal-img/modal-img.component";
import {catchError, map, switchMap, takeUntil} from "rxjs/operators";
import {
  AccountGoodsData,
  Applicability,
  GoodInfoSpResponse, GoodPropertiesDok,
} from "../../../interfaces/account-goods-data";
import {HttpClient} from "@angular/common/http";
import {BaseService} from "../../../services/base.service";
import {LocalStorageService} from "../../../services/local-storage.service";
import {of, Subject, Subscription} from "rxjs";
import * as moment from "moment";
import {cloneDeep} from 'lodash';

@Component({
  selector: 'app-modal-good-information',
  templateUrl: './modal-good-information.component.html',
  styleUrls: ['./modal-good-information.component.scss'],
})

export class ModalGoodInformationComponent extends BaseService implements OnDestroy {
  @ViewChild(ModalImgComponent) modalImgComponentRef: ModalImgComponent;

  private isDestroyed: Subject<boolean> = new Subject();
  private http: HttpClient = inject(HttpClient);
  private localStorage: LocalStorageService = inject(LocalStorageService);

  public isModalGoodInformationShow: boolean = false;
  public isDarkBackground: boolean = false;
  public images: { itemImageSrc: string, thumbnailImageSrc: string }[] = [];
  public responsiveOptions = [];
  public good_name: string = '';
  public properties: { name: string, value: string }[] = [];
  public propertiesDok: GoodPropertiesDok[] = [];
  public pdfs: { description: string, url: string }[] = [];
  public urls = [];
  public articleString: string = '';
  public eanString: string = '';
  public applicability: boolean = false;
  private goodId: number;
  public applicabilityData: Applicability[] = [];
  public searchText: string = '';
  public filteredApplicabilityData: Applicability[] = [];
  public description: string = '';
  public isCertificateShow: boolean = false;
  // Поточне завантаження: при відкритті іншого товару / закритті скасовуємо, щоб пізня відповідь не перекрила вікно
  private loadSubscription: Subscription | null = null;

  openModalGoodInformation(good_id: number) {
    this.isModalGoodInformationShow = false;
    this.isDarkBackground = false;

    this.goodId = good_id;

    this.loadSubscription?.unsubscribe();
    // currency — валюта кабінету (кукі currency_id кабінет не ставить): ціна за літр у тій самій валюті, що й на картці
    this.loadSubscription = this.http.post<AccountGoodsData>(this.apiRoot + 'good/get_good_info', {
      pack_id: good_id,
      currency: this.localStorage.get('currencies'),
    })
      .pipe(
        takeUntil(this.isDestroyed),
        switchMap(data => {
          const hasProps = Array.isArray(data.properties) && data.properties.length > 0;
          const hasTehnoCriteria = Array.isArray(data.tehno_criteria) && data.tehno_criteria.length > 0;
          const hasTehnoPhotos = Array.isArray(data.tehno_photos) && data.tehno_photos.length > 0;
          const hasTehnoData =  Object.keys(data.tehno_data).length > 0;

          // Если в нашей базе есть то возвращаем их
          if (hasProps || hasTehnoCriteria || hasTehnoPhotos || hasTehnoData) {
            return of(data);
          }

          // Иначе делаем запрос и записываем в нашу базу недостающие данные
          return this.http.post<GoodInfoSpResponse>(this.apiRoot + 'good/get_good_info_sp', {
            pack_id: good_id
          })
            .pipe(
              map(sp => {
                if (Array.isArray(sp.tehno_photos)) {
                  data.tehno_photos = sp.tehno_photos;
                }

                if (Array.isArray(sp.tehno_criteria)) {
                  data.tehno_criteria = sp.tehno_criteria;
                }

                return data;
              }),
              // Постачальник не відповів (таймаут/помилка) — показуємо наш локальний опис
              catchError(() => of(data))
            );
        })
      )
      .subscribe((data: AccountGoodsData) => {
        this.good_name = data.good_name;

        this.properties = Array.isArray(data.properties) && data.properties.length ? data.properties : data.tehno_criteria;

        // Ціна за літр — розрахункова характеристика рідин, першим рядком серед характеристик
        const ppl = data.price_per_liter;
        if (ppl && ppl.value > 0) {
          const currency = ppl.currency === 'UAH' ? 'грн' : ppl.currency;
          this.properties = [
            { name: 'Ціна за літр', value: `${ppl.value.toFixed(2)} ${currency}/л` },
            ...(Array.isArray(this.properties) ? this.properties : []),
          ];
        }

        if (Array.isArray(data.cat1)) {
          this.propertiesDok = data.cat1;
        } else {
          this.propertiesDok = [];
        }

        this.description = data.description.replaceAll(/\\n/g, '<br>');

        const article = data.article;
        this.articleString = article.join(', ');

        const ean = data.ean;
        this.eanString = ean.join(', ');

        const normalImages = Array.isArray(data.images)
          ? data.images
            .filter(img => img.file_available)
            .map(img => ({
              itemImageSrc: 'https://maslotochka.com/plugins/goodphoto.php?file=' + img.media_source,
              thumbnailImageSrc: 'https://maslotochka.com/plugins/goodphoto.php?file=' + img.media_source
            }))
          : [];

        const tehnoImages = Array.isArray(data.tehno_photos)
          ? data.tehno_photos.map(p => ({
            itemImageSrc: p.image,
            thumbnailImageSrc: p.image
          }))
          : [];

        this.images = normalImages.length > 0 ? normalImages : tehnoImages;

        if (Array.isArray(data.pdfs)) {
          this.pdfs = data.pdfs.filter(pdf => pdf.file_available).map(pdf => ({
            description: pdf.description,
            url: 'https://maslotochka.com/plugins/goodpdf.php?file=' + pdf.media_source
          }));
        }

        if (Array.isArray(data.urls)) {
          this.urls = data.urls.filter((url) => url.is_url_accessible).map(url => ({
            description: url.description,
            ...url,
            // без bypassSecurityTrust*: [href] сам пропускає http(s) і блокує javascript:/data:
            safeMediaSource: url.media_source,
          }))
        }

        this.responsiveOptions = [
          {
            breakpoint: '1024px',
            numVisible: 5
          },
          {
            breakpoint: '768px',
            numVisible: 3
          },
          {
            breakpoint: '560px',
            numVisible: 3
          }
        ];

        this.isModalGoodInformationShow = true;
        this.isDarkBackground = true;
      });
  }

  applicabilityInfo() {
    this.http.post(this.apiRoot + 'good/get_applicability', {
      pack_id: this.goodId,
    })
      .pipe(takeUntil(this.isDestroyed))
      .subscribe((data: AccountGoodsData) => {
        this.applicabilityData = data.applicability;
        this.filteredApplicabilityData = data.applicability;

        if (this.applicabilityData && Array.isArray(this.applicabilityData)) {
          for (let app of this.applicabilityData) {
            app.PCS_CI_FROM = app.PCS_CI_FROM ? moment(app.PCS_CI_FROM).format('YYYY') : '';
            app.PCS_CI_TO = app.PCS_CI_TO ? moment(app.PCS_CI_TO).format('YYYY') : '';
            app.PCS_CAPACITY_TECH = app.PCS_CAPACITY_TECH ? parseFloat(app.PCS_CAPACITY_TECH).toString() : '';
            app.PCS_POWER_KW = app.PCS_POWER_KW ? parseFloat(app.PCS_POWER_KW).toString() : '';
            app.PCS_POWER_PS = app.PCS_POWER_PS ? parseFloat(app.PCS_POWER_PS).toString() : '';
          }
        }

        this.applicability = !this.applicability;
      });
  }

  certificate() {
    this.isCertificateShow = !this.isCertificateShow;
  }

  filterApplicability() {
    const applicability = cloneDeep(this.applicabilityData);

    const search = this.searchText.toLowerCase().trim().split(/\s+/).filter(s => s);

    if (search.length === 0) {
      this.filteredApplicabilityData = applicability;
    } else {
      this.filteredApplicabilityData = applicability.filter(app => {
        let applicabilityFind = true;

        for (let searchItem of search) {
          let searchItemFind = false;
          let years = parseInt(searchItem);

          for (let item in app) {
            if (typeof app[item] === 'string') {
              const loverItem = app[item].toLowerCase();

              if (loverItem.includes(searchItem)) {
                searchItemFind = true;
                break;
              }
            }
          }

          // Порожній PCS_CI_TO при заповненому PCS_CI_FROM - модель ще випускається: верхня межа - поточний рік
          // (раніше years <= '' завжди false і такі моделі за роком не знаходились)
          let yearTo = app.PCS_CI_TO;
          if (!yearTo && app.PCS_CI_FROM) {
            yearTo = moment().format('YYYY');
          }

          if (years >= app.PCS_CI_FROM && years <= yearTo) {
            searchItemFind = true;
          }

          if (searchItemFind === false) {
            applicabilityFind = false;
          }
        }

        return applicabilityFind;
      });
    }
  }

  clearSearch() {
    this.filteredApplicabilityData = cloneDeep(this.applicabilityData);
    this.searchText = '';
  }

  openFullSizePhotoModal() {
    this.modalImgComponentRef.openPhoto();
  }

  closeModal() {
    this.loadSubscription?.unsubscribe();
    this.loadSubscription = null;
    this.images = [];
    this.searchText = '';
    this.isModalGoodInformationShow = false;
    this.applicability = false;
    this.isCertificateShow = false;
    this.isDarkBackground = false;
  }

  ngOnDestroy() {
    this.isDestroyed.next(true);
    this.isDestroyed.complete();
  }
}

